import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AssetItemsResult, GridRequestQuery } from '@api';
import type { CellContext, HeaderCellContext } from '@swimlane/ngx-datatable';
import { SelectionType, TableColumn } from '@swimlane/ngx-datatable';
import { map } from 'rxjs';
import { MasterType } from '../../enumerations/master-type';
import { tryParseJSON } from '../../extensions/string.extensions';
import { Asset } from '../../services/api/model/asset';
import { ComponentBaseDirective } from '../component.base';

@Component({
    selector: 'app-asset-list',
    templateUrl: './asset-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AssetListComponent extends ComponentBaseDirective implements OnChanges {
    @Input() public assets: AssetItemsResult;
    @Input() public isConfigMode = false;

    @Output() public selectionChange = new EventEmitter<Asset>();
    @Output() public selectionEdit = new EventEmitter<Asset>();
    @Output() public activationChange = new EventEmitter<Asset>();
    @Output() public gridRequestEvent = new EventEmitter<GridRequestQuery>();

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    @Output() public rowSelectedEvent = new EventEmitter<any>();

    @ViewChild('deviceStatusTemplate', { static: true }) private deviceStatusTemplate: TemplateRef<CellContext>;
    @ViewChild('configModeHeaderTemplate', { static: true }) private configModeHeaderTemplate: TemplateRef<HeaderCellContext>;
    @ViewChild('configModeTemplate', { static: true }) private configModeTemplate: TemplateRef<CellContext>;
    @ViewChild('metadataTemplate', { static: true }) private metadataTemplate: TemplateRef<CellContext>;
    @ViewChild('commandsTemplate', { static: true }) private commandsTemplate: TemplateRef<CellContext>;
    @ViewChild('nameTemplate', { static: true }) private nameTemplate: TemplateRef<CellContext>;

    public columns$ = this.getColumns();

    public SelectionType = SelectionType;

    constructor() {
        super();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.isConfigMode?.firstChange === false) {
            this.columns$ = this.getColumns();
        }
    }

    public emitSelect([asset]: Asset[]) {
        this.selectionChange.emit(asset);
    }

    public emitEdit(asset: Asset) {
        this.selectionEdit.emit(asset);
    }

    public emitActivationChange(asset: Asset) {
        const element = document.getElementById(`ActivatedCheckbox${asset.id}`) as HTMLInputElement;

        const changedAsset = Object.assign({}, asset);
        changedAsset.isActivated = element.checked;

        this.activationChange.emit(changedAsset);
    }

    public getAssetMetadata(assetMetaData: string) {
        const metadata = tryParseJSON(assetMetaData, {});

        for (const key in metadata) {
            if (key !== 'masterType' && key !== 'masterPort' && key !== 'masterIdentifier') {
                delete metadata[key];
            }
        }

        return `${this.translationService.translate('entities.master')}: ${MasterType[metadata.masterType ?? 0]}<br/>${this.translationService.translate('entities.port')}: ${metadata.masterPort ?? 0}<br/>${this.translationService.translate('entities.mac')}: ${metadata.masterIdentifier}`;
    }

    public gridRequested(gridRequestQuery: GridRequestQuery) {
        this.gridRequestEvent.emit(gridRequestQuery);
    }

    private getColumns() {
        return this.translationService.afterLanguageChanged$.pipe(
            map(() => {
                const columns: TableColumn[] = [
                    { width: 30, maxWidth: 30, sortable: false, resizeable: false, cellTemplate: this.deviceStatusTemplate, canAutoResize: false },
                    { name: this.translationService.translate('defaults.name'), prop: 'name', resizeable: true, canAutoResize: true, cellTemplate: this.nameTemplate },
                    { width: 140, canAutoResize: false, sortable: false, resizeable: false, headerTemplate: this.configModeHeaderTemplate, cellTemplate: this.configModeTemplate },
                    { name: this.translationService.translate('defaults.metadata'), prop: 'Metadata', width: 100, maxWidth: 100, sortable: false, resizeable: false, cellTemplate: this.metadataTemplate, canAutoResize: false },
                    { width: 50, maxWidth: 50, sortable: false, resizeable: false, cellTemplate: this.commandsTemplate, canAutoResize: false },
                ];

                const configModeIndex = 2;
                const configModeColumnWidth = this.isConfigMode ? 180 : 1;

                columns[configModeIndex].width = configModeColumnWidth;
                columns[configModeIndex].cellClass = this.isConfigMode ? '' : 'p-0';

                return columns;
            })
        );
    }
}
